import { useState, useEffect, useMemo } from 'react'
import { adminApi } from '../services/ApiService'
import { Thermometer, Droplets, Activity, Clock, FlaskConical, Zap } from 'lucide-react'
import './TelemetryChart.css'

const SERIES = [
  { key: 'temperature', label: 'Temperatura', unit: '°C', color: '#ef4444', icon: Thermometer },
  { key: 'humidity', label: 'Humedad', unit: '%', color: '#3b82f6', icon: Droplets },
  { key: 'dpv', label: 'DPV', unit: 'kPa', color: '#10b981', icon: Activity },
  { key: 'ph', label: 'pH', unit: '', color: '#22c55e', icon: FlaskConical },
  { key: 'tds', label: 'TDS', unit: 'ppm', color: '#f59e0b', icon: Zap },
  { key: 'waterTemp', label: 'Temp Agua', unit: '°C', color: '#06b6d4', icon: Thermometer }
]

export default function TelemetryChart({ mac }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [hours, setHours] = useState(24)
  const [activeSeries, setActiveSeries] = useState([])

  // Auto-select series that have data
  useEffect(() => {
    if (!data.length) return
    const available = SERIES.filter(s => data.some(d => d[s.key] != null)).map(s => s.key)
    setActiveSeries(prev => prev.length ? prev.filter(k => available.includes(k)) : available.slice(0, 3))
  }, [data])

  const availableSeries = useMemo(() => {
    if (!data.length) return SERIES
    return SERIES.filter(s => data.some(d => d[s.key] != null))
  }, [data])

  useEffect(() => {
    if (!mac) return
    let cancelled = false
    setLoading(true)
    adminApi.get(`/admin/telemetry/${encodeURIComponent(mac)}?hours=${hours}`)
      .then(res => {
        if (!cancelled && res.success) setData(res.points || [])
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [mac, hours])

  const chartData = useMemo(() => {
    if (!data.length) return null

    const W = 600, H = 180, PAD_L = 45, PAD_R = 10, PAD_T = 10, PAD_B = 28
    const plotW = W - PAD_L - PAD_R
    const plotH = H - PAD_T - PAD_B

    const result = {}

    for (const s of SERIES) {
      if (!activeSeries.includes(s.key)) continue

      const vals = data.map(d => d[s.key]).filter(v => v != null)
      if (!vals.length) continue

      const min = Math.min(...vals)
      const max = Math.max(...vals)
      const range = max - min || 1

      const points = data
        .map((d, i) => {
          const v = d[s.key]
          if (v == null) return null
          const x = PAD_L + (i / (data.length - 1 || 1)) * plotW
          const y = PAD_T + plotH - ((v - min) / range) * plotH
          return { x, y, v }
        })
        .filter(Boolean)

      const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
      const areaD = pathD + ` L${points[points.length - 1].x.toFixed(1)},${PAD_T + plotH} L${points[0].x.toFixed(1)},${PAD_T + plotH} Z`

      result[s.key] = { points, pathD, areaD, min, max, color: s.color }
    }

    // Time labels
    const first = new Date(data[0].timestamp)
    const last = new Date(data[data.length - 1].timestamp)
    const timeLabels = [0, 0.25, 0.5, 0.75, 1].map(pct => {
      const t = new Date(first.getTime() + pct * (last.getTime() - first.getTime()))
      return {
        x: PAD_L + pct * plotW,
        label: t.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      }
    })

    return { W, H, PAD_L, PAD_R, PAD_T, PAD_B, plotW, plotH, series: result, timeLabels }
  }, [data, activeSeries])

  const toggleSeries = (key) => {
    setActiveSeries(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  return (
    <div className="tc-container">
      <div className="tc-header">
        <h4 className="tc-title">
          <Clock size={14} /> Historial de Telemetría
        </h4>
        <div className="tc-time-btns">
          {[1, 6, 12, 24].map(h => (
            <button key={h} className={`tc-tbtn ${hours === h ? 'active' : ''}`} onClick={() => setHours(h)}>
              {h}h
            </button>
          ))}
        </div>
      </div>

      {/* Series toggles */}
      <div className="tc-legend">
        {availableSeries.map(s => {
          const Icon = s.icon
          return (
            <button
              key={s.key}
              className={`tc-legend-btn ${activeSeries.includes(s.key) ? 'active' : ''}`}
              onClick={() => toggleSeries(s.key)}
              style={{ '--sc': s.color }}
            >
              <Icon size={12} /> {s.label}
            </button>
          )
        })}
      </div>

      {loading && (
        <div className="tc-loading">
          <div className="tc-spinner" /> Cargando datos...
        </div>
      )}

      {!loading && !data.length && (
        <div className="tc-empty">Sin datos de telemetría para este período</div>
      )}

      {!loading && chartData && Object.keys(chartData.series).length > 0 && (
        <div className="tc-chart-wrap">
          <svg viewBox={`0 0 ${chartData.W} ${chartData.H}`} className="tc-svg">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(pct => (
              <line
                key={pct}
                x1={chartData.PAD_L}
                y1={chartData.PAD_T + pct * chartData.plotH}
                x2={chartData.PAD_L + chartData.plotW}
                y2={chartData.PAD_T + pct * chartData.plotH}
                stroke="rgba(148,163,184,0.08)"
                strokeWidth="0.5"
              />
            ))}

            {/* Areas and lines */}
            {Object.entries(chartData.series).map(([key, s]) => (
              <g key={key}>
                <defs>
                  <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={s.areaD} fill={`url(#grad-${key})`} />
                <path d={s.pathD} fill="none" stroke={s.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            ))}

            {/* Y-axis labels for each active series */}
            {Object.entries(chartData.series).map(([key, s], idx) => (
              <g key={`y-${key}`}>
                <text x={chartData.PAD_L - 4} y={chartData.PAD_T + 4} textAnchor="end" fontSize="8" fill={s.color} fontWeight="600">{s.max.toFixed(1)}</text>
                <text x={chartData.PAD_L - 4} y={chartData.PAD_T + chartData.plotH + 4} textAnchor="end" fontSize="8" fill={s.color}>{s.min.toFixed(1)}</text>
              </g>
            ))}

            {/* Time labels */}
            {chartData.timeLabels.map((t, i) => (
              <text key={i} x={t.x} y={chartData.H - 4} textAnchor="middle" fontSize="8" fill="#64748b">{t.label}</text>
            ))}
          </svg>

          {/* Min/Max summary */}
          <div className="tc-summary">
            {Object.entries(chartData.series).map(([key, s]) => {
              const ser = SERIES.find(x => x.key === key)
              return (
                <div key={key} className="tc-sum-item" style={{ color: s.color }}>
                  <span className="tc-sum-label">{ser.label}</span>
                  <span className="tc-sum-range">{s.min.toFixed(1)} – {s.max.toFixed(1)} {ser.unit}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
